import React, { FC, useEffect, useRef } from 'react';
import { Hub } from '@aws-amplify/core';
import Auth from '@aws-amplify/auth';

import { BootstrapQuery, useBootstrapQuery } from 'api';
import { ErrorPage, Loaders } from 'components';
import { useCognitoUser, useGlobalClient } from 'hooks';
import { USER_DISABLED } from 'features/auth/constants';
import * as paths from 'paths';

import Providers from './Providers';
import Connections from './Connections';
import ProtectedRoutes from './Routes';
import AuthError from 'features/auth/containers/AuthError';
import { useHistory } from 'react-router-dom';

const MIN_REFRESH_TIME = 60000;

const ProtectedIndex: FC = () => {
  const auth = useCognitoUser();
  const variables = { user_id: auth.id };
  const { data, error, isLoading } = useBootstrapQuery<BootstrapQuery, Error>(variables);
  const lastRefresh = useRef(0);
  const publicClient = useGlobalClient();
  const history = useHistory();

  useEffect(() => {
    Hub.listen('auth', handleAuth);
    checkForClientMismatch();
  }, []);

  // This is a hack to work around the fact that cognito currently ignores the clientMetadata in refreshToken requests.
  // When we detect that the client_id in the token differs from the client_id from the domain, we update the user attributes
  // with the correct client id, and then generate and store a new token.
  async function forceRefreshToken() {
    const now = new Date().getTime();
    if (now - lastRefresh.current < MIN_REFRESH_TIME) {
      return;
    }
    lastRefresh.current = now;
    try {
      const token = currentIdTokenFromLocalStorage();
      const cognitoUser = await Auth.currentAuthenticatedUser();
      await Auth.updateUserAttributes(cognitoUser, { 'custom:client_id': publicClient.id });
      const currentSession = await Auth.currentSession();
      cognitoUser.refreshSession((currentSession as any).refreshToken, (err: any, session: any) => {
        console.log('forcedTokenRefresh', err, session);
        if (
          session.idToken.payload['custom:client_id'] != publicClient.id ||
          token['custom:client_id'] != publicClient.id
        ) {
          location.reload(); // We reload because a request may have gone through with the wrong token
        }
      });
    } catch (e) {
      console.log('forcedTokenRefresh:fail', e);
      history.push(paths.logout);
    }
  }

  function handleAuth(event: any) {
    const eventType = event?.payload?.event;
    if (eventType === 'tokenRefresh') {
      checkForClientMismatch();
    }
  }

  /**
   * Handles case where cognito refresh token contains a different client ID
   * If a different client ID is given by Cognito, we redirect to root domain
   * https://forums.aws.amazon.com/thread.jspa?messageID=910021&#910021
   */
  function checkForClientMismatch() {
    const { authenticatedUser } = auth;
    const clientId = (authenticatedUser as any)?.attributes['custom:client_id'];
    // We read the token from local storage here because sometimes the attributes from currentAuthenticatedUser
    // are not synchronized with localStorage
    const token = currentIdTokenFromLocalStorage();
    if (clientId != publicClient.id || token['custom:client_id'] != publicClient.id) {
      void forceRefreshToken();
    }
  }

  function currentIdTokenFromLocalStorage() {
    const { authenticatedUser } = auth;
    return (authenticatedUser as any)?.attributes;
  }

  if (isLoading) {
    return <Loaders.Startup />;
  }

  if (error?.message === USER_DISABLED) {
    return <AuthError code="user_profile_suspended" />;
  }

  if (error) {
    return <ErrorPage />;
  }

  return (
    <Providers bootstrapQuery={data}>
      <Connections />
      <ProtectedRoutes />
    </Providers>
  );
};

export default ProtectedIndex;
